/**
 * @file Shared grouped kanban view shell and WIP rendering mechanics.
 *
 * Callers own domain item selection, card adapters, mutation callbacks, and
 * Firestore reads. This module owns reusable grouped kanban layout behavior.
 */

import { appData, state } from "../../app/state/state.js";
import {
  applyBoardRuntimePipeline,
  hasActiveFilters,
  hasActiveSearch,
} from "./boardRuntime.js";
import { buildGroupedItems } from "./groupedViewEngine.js";
import { getBoardCollectionNameForItemType } from "./boardRegistry.js";
import {
    getGridColsClass,
} from "../utils/displayUtils.js";

function getCollectionTypeForItemType(itemType) {
  return getBoardCollectionNameForItemType(itemType);
}

function shouldRenderClosedLoadMore(group, itemType) {
  const collectionType = getCollectionTypeForItemType(itemType);
  if (!collectionType) {
    return false;
  }
  const paginationState = state.closedItemsPagination?.[collectionType];
  if (!state.showClosed || !paginationState?.hasMore) {
    return false;
  }
  return group?.id === "closed" || group?.isClosed === true;
}

function renderClosedLoadMoreButton(itemType) {
  const collectionType = getCollectionTypeForItemType(itemType);
  if (!collectionType) {
    return "";
  }
  return `
        <div class="pt-2">
            <button
                type="button"
                class="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)] load-more-closed-btn"
                data-collection-type="${collectionType}"
            >
                Load More
            </button>
        </div>
    `;
}

async function preserveLoadMoreViewport(button, action) {
  const columnId = button?.closest("[data-column-id]")?.dataset?.columnId || null;
  const scrollContainer = button?.closest(".space-y-3") || null;
  const beforeScrollTop = scrollContainer?.scrollTop ?? 0;
  const beforeActiveElement = document.activeElement;

  if (beforeActiveElement instanceof HTMLElement) {
    beforeActiveElement.blur();
  }

  await action();

  requestAnimationFrame(() => {
    if (!columnId) {
      return;
    }

    const nextColumn = document.querySelector(`[data-column-id="${columnId}"]`);
    const nextScrollContainer = nextColumn?.querySelector(".space-y-3");
    if (!nextScrollContainer) {
      return;
    }

    nextScrollContainer.scrollTop = beforeScrollTop;

    requestAnimationFrame(() => {
      nextScrollContainer.scrollTop = beforeScrollTop;
    });
  });
}

function getBoardEmptyStateCopy(viewKey, singularLabel, sourceItems = [], renderedItems = []) {
  const hasSearch = hasActiveSearch(viewKey);
  const hasFilters = hasActiveFilters(viewKey);
  const sourceCount = Array.isArray(sourceItems) ? sourceItems.length : 0;
  const renderedCount = Array.isArray(renderedItems) ? renderedItems.length : 0;

  if (renderedCount > 0) {
    return null;
  }

  if ((hasSearch || hasFilters) && sourceCount > 0) {
    return {
      title: "No results",
      body: hasSearch && hasFilters ?
        "No items match the current search and filters." :
        hasSearch ?
          "No items match the current search." :
          "No items match the current filters.",
    };
  }

  return {
    title: `No ${singularLabel}s yet`,
    body: `There are no ${singularLabel}s to display on this board yet.`,
  };
}

/**
 * Renders a reusable board state card for empty and error states.
 * @param {object} options - State card options.
 * @param {string} options.title - State card title.
 * @param {string} options.body - State card body text.
 * @param {string} [options.tone] - Visual tone.
 * @returns {string} Board state card HTML.
 */
export function renderBoardStateCard({ title, body, tone = "empty" }) {
  const icon = tone === "error" ? "error" : "inbox";
  const iconClasses = tone === "error" ? "text-red-400" : "text-gray-300";

  return `
        <div class="col-span-full rounded-lg border border-gray-200 bg-white px-6 py-10 text-center shadow-sm">
            <span class="material-symbols-outlined text-[32px] ${iconClasses}">${icon}</span>
            <h3 class="mt-3 text-base font-semibold text-gray-800">${title}</h3>
            <p class="mt-1 text-sm text-gray-500">${body}</p>
        </div>
    `;
}

function attachClosedLoadMoreHandlers(board, onLoadMoreClosed) {
  board.querySelectorAll(".load-more-closed-btn").forEach((button) => {
    button.onclick = async (event) => {
      event.stopPropagation();
      const collectionType = button.dataset.collectionType;
      if (!collectionType || typeof onLoadMoreClosed !== "function") {
        return;
      }
      button.disabled = true;
      try {
        await preserveLoadMoreViewport(button, async () => {
          await onLoadMoreClosed(collectionType);
        });
      } finally {
        button.disabled = false;
      }
    };
  });
}

/**
 * Renders the shared grouped kanban shell.
 * @param {HTMLElement} board - Board container element.
 * @param {object} options - Grouped kanban options.
 * @param {string} options.viewKey - Board view key.
 * @param {string} options.itemType - Board item type.
 * @param {string} options.singularLabel - Singular item label.
 * @param {Array<object>} options.sourceItems - Pre-runtime source items.
 * @param {Array<object>} options.items - Items to group and render.
 * @param {string} options.groupBy - Grouping mode.
 * @param {string} options.dateGroupingMode - Date grouping mode.
 * @param {Array<object>} [options.taskList] - Task list for date grouping.
 * @param {Function} options.renderCard - Card rendering adapter.
 * @param {Function} [options.onLoadMoreClosed] - Closed load-more callback.
 * @returns {void}
 */
export function renderGroupedKanbanBoard(board, {
  viewKey,
  itemType,
  singularLabel,
  sourceItems,
  items,
  groupBy,
  dateGroupingMode,
  taskList = appData.tasks,
  renderCard,
  onLoadMoreClosed,
}) {
  if (!board) {
    console.error("renderGroupedKanbanBoard: Board element not provided.");
    return;
  }

  const groups = buildGroupedItems({
    itemType,
    items: items || [],
    groupBy,
    dateGroupingMode,
    showClosed: state.showClosed || false,
    taskList,
  }).filter((group) => {
    if (group.items.length > 0) {
      return true;
    }
    if (group.isFallback) {return false;}
    return group.id !== "none" &&
      group.id !== "uncategorised" &&
      group.id !== "closed";
  });

  if ((items || []).length === 0) {
    board.className = "grid grid-cols-1 gap-6";
    const emptyState = getBoardEmptyStateCopy(viewKey, singularLabel, sourceItems, items);
    board.innerHTML = renderBoardStateCard(emptyState || {
      title: "Nothing to show",
      body: "There is nothing to display right now.",
    });
    return;
  }

  const gridClass = getGridColsClass(Math.max(groups.length, 1));
  board.className = `grid grid-cols-1 ${gridClass} gap-6`;
  board.innerHTML = groups.map((group) => {
    const groupHeaderDot = group.color ?
      `<span class="w-3 h-3 rounded-full mr-2" style="background-color: ${group.color};"></span>` :
      "";
    const groupCardsHtml = group.items.map((item, index) => `
            <div id="kanban-${itemType}-${group.id}-${item.id}-${index}">
                ${renderCard(item)}
            </div>
        `).join("");

    return `
            <div class="bg-white p-4 rounded-lg shadow kanban-column" data-column-id="${group.id}">
                <h3 class="font-bold text-lg mb-4 text-gray-800 flex items-center capitalize">
                    ${groupHeaderDot}
                    ${group.title}
                    <span class="text-sm font-normal text-gray-500 ml-2">${group.items.length}</span>
                </h3>
                <div class="space-y-3">
                    ${groupCardsHtml}
                    ${shouldRenderClosedLoadMore(group, itemType) ? renderClosedLoadMoreButton(itemType) : ""}
                </div>
            </div>
        `;
  }).join("");

  attachClosedLoadMoreHandlers(board, onLoadMoreClosed);
}

function buildOpenTasksByParent(tasks = []) {
  const groupedTasks = new Map();

  (tasks || []).forEach((task) => {
    if (!groupedTasks.has(task.parentId)) {
      groupedTasks.set(task.parentId, []);
    }

    groupedTasks.get(task.parentId).push(task);
  });

  return groupedTasks;
}

function attachParentKanbanListeners(board, {
  quickAddDatasetKey,
  onQuickAddTask,
  onOpenDetails,
}) {
  board.querySelectorAll(".kanban-card").forEach((element) => {
    element.onclick = null;
    element.onclick = (event) => {
      const quickAddButton = event.target.closest(".quick-add-task-btn");

      if (quickAddButton) {
        event.stopPropagation();
        const parentId = quickAddButton.dataset[quickAddDatasetKey];
        if (typeof onQuickAddTask === "function") {
          onQuickAddTask(parentId);
        }
        return;
      }

      if (!event.target.closest("button") && typeof onOpenDetails === "function") {
        onOpenDetails(element.dataset.id);
      }
    };
  });
}

/**
 * Renders a parent-board WIP kanban using the shared grouped shell.
 * @param {HTMLElement} board - Board container element.
 * @param {object} options - WIP kanban options and callbacks.
 * @param {string} options.viewKey - Board view key.
 * @param {string} options.itemType - Board item type.
 * @param {string} options.parentType - Task parent type for fetching.
 * @param {Array<object>} options.items - Parent board items.
 * @param {Function} options.renderCard - WIP card rendering adapter.
 * @param {string} options.quickAddDatasetKey - Quick-add button dataset key.
 * @param {Function} options.fetchOpenTasksForParentType - Task fetch callback.
 * @param {Function} options.onQuickAddTask - Quick-add callback.
 * @param {Function} options.onOpenDetails - Detail navigation callback.
 * @param {Function} [options.onLoadMoreClosed] - Closed load-more callback.
 * @returns {Promise<void>}
 */
export async function renderParentWIPKanbanBoard(board, {
  viewKey,
  itemType,
  parentType,
  items,
  renderCard,
  quickAddDatasetKey,
  fetchOpenTasksForParentType,
  onQuickAddTask,
  onOpenDetails,
  onLoadMoreClosed,
}) {
  if (!board) {
    console.error(`renderParentWIPKanban: Board element not provided for ${itemType}.`);
    return;
  }

  const settings = state.viewSettings?.[viewKey];
  if (!settings) {
    console.error(`renderParentWIPKanban: View settings not loaded for ${viewKey}.`);
    return;
  }

  board.className = "flex justify-center items-center p-20 min-h-[400px]";
  board.innerHTML = `<div class="text-gray-500 animate-pulse">Loading Detail View... ⏳</div>`;

  try {
    const allOpenTasks = await fetchOpenTasksForParentType(parentType);
    const openTasksByParent = buildOpenTasksByParent(allOpenTasks);
    const visibleItems = applyBoardRuntimePipeline({
      viewKey,
      itemType,
      items: items || [],
      taskList: allOpenTasks,
    });

    renderGroupedKanbanBoard(board, {
      viewKey,
      itemType,
      singularLabel: itemType,
      sourceItems: items || [],
      items: visibleItems,
      groupBy: settings.boardGroupBy,
      dateGroupingMode: settings.boardDateGroupingMode,
      taskList: allOpenTasks,
      renderCard: (item) => renderCard(item, openTasksByParent.get(item.id) || []),
      onLoadMoreClosed,
    });

    attachParentKanbanListeners(board, {
      quickAddDatasetKey,
      onQuickAddTask,
      onOpenDetails,
    });
  } catch (error) {
    console.error(`Error rendering ${itemType} WIP Kanban:`, error);
    board.className = "grid grid-cols-1 gap-6";
    board.innerHTML = `<p class="text-red-500 col-span-full text-center py-8">Error loading detail view: ${error.message}</p>`;
  }
}
